import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  YouTube,
  VideoLibrary,
  Translate,
  Analytics,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  StyledContainer,
  StyledPaper,
  StyledSectionHeaderWithBadge,
  StyledEmptyState,
  StyledEmptyStateIcon,
} from './styles';
import VideoCard from './VideoCard';
import BulkAnalyzeModal from './BulkAnalyzeModal';
import { API_URL } from './config';
import axios from 'axios';
import { trackEvent } from './analytics';

function Channel() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalVideos, setTotalVideos] = useState(0);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('viewCount');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateFilter, setDateFilter] = useState('all');
  const [minOutlierScore, setMinOutlierScore] = useState('all');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showTranslation, setShowTranslation] = useState(true);
  const [bulkAnalyzeModalOpen, setBulkAnalyzeModalOpen] = useState(false);
  const [showBulkAnalyze, setShowBulkAnalyze] = useState(false);
  const [success, setSuccess] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  // Debounce query to avoid fetching on each keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 1000);
    return () => clearTimeout(handle);
  }, [query]);

  const searchSnapshot = React.useMemo(
    () => ({
      query: debouncedQuery,
      sortBy,
      order: sortOrder,
      dateFilter,
      minOutlierScore,
      searchOnTranslation: showTranslation,
      channelId,
      channelName: channel?.channelName,
    }),
    [
      debouncedQuery,
      sortBy,
      sortOrder,
      dateFilter,
      minOutlierScore,
      showTranslation,
      channelId,
      channel?.channelName,
    ],
  );

  const channelOpenTracked = React.useRef(null);
  useEffect(() => {
    if (!channel || !channelId) return;
    if (channelOpenTracked.current === channelId) return;
    channelOpenTracked.current = channelId;
    trackEvent('channel_open', {
      scope: 'channel',
      context: {
        channelId,
        channelTitle: channel.channelName,
      },
      payload: { channelId, channelName: channel.channelName },
    });
  }, [channel, channelId]);

  const fetchChannelData = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError('');
      }

      let channelTitleForAnalytics = channel?.channelName;

      // Fetch channel details (only on initial load)
      if (!isLoadMore) {
        const channelResponse = await axios.get(`${API_URL}/api/channels/${channelId}`);
        const channelData = channelResponse.data;
        setChannel(channelData);
        setIsFavorite(channelData.isFavorite || false);
        channelTitleForAnalytics = channelData.channelName;
      }

      // Fetch videos in this channel with pagination
      const offset = isLoadMore ? videos.length : 0;
      const params = new URLSearchParams({
        sortBy,
        order: sortOrder,
        offset: String(offset),
        limit: String(50),
        dateFilter,
        minOutlierScore,
      });
      if (debouncedQuery && debouncedQuery.length > 0) {
        params.set('query', debouncedQuery);
      }
      if (showTranslation) {
        params.set('searchOnTranslation', 'true');
      }

      const videosResponse = await axios.get(
        `${API_URL}/api/channels/${channelId}/videos?${params.toString()}`,
      );
      const videosData = videosResponse.data;

      if (isLoadMore) {
        setVideos((prev) => [...prev, ...videosData.videos]);
      } else {
        setVideos(videosData.videos);
      }

      setHasMore(videosData.hasMore);
      setTotalVideos(videosData.total);

      if (isLoadMore) {
        trackEvent('load_more', {
          scope: 'channel',
          context: {
            channelId,
            channelTitle: channelTitleForAnalytics,
          },
          payload: {
            ...searchSnapshot,
            channelName: channelTitleForAnalytics,
            offset: videos.length,
          },
        });
      } else {
        trackEvent('search', {
          scope: 'channel',
          context: {
            channelId,
            channelTitle: channelTitleForAnalytics,
          },
          payload: {
            ...searchSnapshot,
            channelName: channelTitleForAnalytics,
            total: videosData.total,
          },
        });
      }
    } catch (error) {
      console.error('Error fetching channel data:', error);
      setError(error.message || 'Failed to load channel');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreVideos = React.useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchChannelData(true);
    }
  }, [loadingMore, hasMore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Intersection Observer for infinite scrolling
  const observerRef = React.useRef();
  const loadMoreRef = React.useCallback(
    (node) => {
      if (loadingMore || !hasMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadMoreVideos();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loadingMore, hasMore, loadMoreVideos],
  );

  useEffect(() => {
    // Reset videos when sort parameters change
    setVideos([]);
    setHasMore(false);
    setTotalVideos(0);
    fetchChannelData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, sortBy, sortOrder, dateFilter, minOutlierScore, debouncedQuery]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() === 'z') {
        setShowBulkAnalyze(true);
      }
    };

    const handleKeyUp = (event) => {
      if (event.key.toLowerCase() === 'z') {
        setShowBulkAnalyze(false);
      }
    };

    const handleWindowBlur = () => {
      setShowBulkAnalyze(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const handleBulkAnalyzeOpen = () => {
    setBulkAnalyzeModalOpen(true);
  };

  const handleBulkAnalyzeClose = () => {
    setBulkAnalyzeModalOpen(false);
  };

  const handleToggleFavorite = async () => {
    try {
      setTogglingFavorite(true);
      const newFavoriteStatus = !isFavorite;
      await axios.put(`${API_URL}/api/channels/${channelId}/favorite`, {
        isFavorite: newFavoriteStatus,
      });

      setIsFavorite(newFavoriteStatus);
      setSuccess(
        newFavoriteStatus ? 'Channel added to favorites' : 'Channel removed from favorites',
      );
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setError('Failed to update favorite status');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTogglingFavorite(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <StyledContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </StyledContainer>
      </Container>
    );
  }

  if (error || !channel) {
    return (
      <Container maxWidth="xl">
        <StyledContainer>
          <StyledPaper>
            <Alert severity="error">{error || 'Channel not found'}</Alert>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }}
              sx={{ mt: 2 }}
            >
              Back
            </Button>
          </StyledPaper>
        </StyledContainer>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <StyledContainer>
        <StyledPaper>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {channel.avatarUrl && (
                  <Avatar
                    src={channel.avatarUrl}
                    sx={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
                    }}
                  >
                    <YouTube />
                  </Avatar>
                )}
                <StyledSectionHeaderWithBadge>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h5" fontWeight={600}>
                        {channel.channelName || channelId}
                      </Typography>
                    </Box>
                    {channel.subscriberCount && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {channel.subscriberCount.toLocaleString()} subscribers
                      </Typography>
                    )}
                  </Box>
                </StyledSectionHeaderWithBadge>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                <IconButton
                  onClick={handleToggleFavorite}
                  disabled={togglingFavorite}
                  sx={{
                    color: isFavorite ? '#fbbf24' : 'text.secondary',
                    '&:hover': {
                      color: '#fbbf24',
                      backgroundColor: 'rgba(251, 191, 36, 0.1)',
                    },
                  }}
                >
                  {isFavorite ? <Star /> : <StarBorder />}
                </IconButton>
              </Tooltip>
              {(videos.some((video) => video.translation) ||
                videos.some((video) => video.channelNameTranslation)) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showTranslation}
                      onChange={(e) => setShowTranslation(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#6366f1',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#6366f1',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'rgba(148, 163, 184, 0.3)',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Translate sx={{ fontSize: 18 }} />
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        Translation
                      </Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      color: 'text.primary',
                    },
                  }}
                />
              )}
              {showBulkAnalyze && (
                <Tooltip
                  title="Generate Video Summaries, Analyzing Writing Styles, And More"
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: { textAlign: 'center' },
                    },
                  }}
                >
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<Analytics />}
                      onClick={handleBulkAnalyzeOpen}
                      disabled={videos.length === 0}
                      sx={{
                        borderRadius: 3,
                        padding: '8px 20px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                        '&:hover': {
                          boxShadow: '0 12px 48px rgba(16, 185, 129, 0.4)',
                        },
                        '&:disabled': {
                          background: 'rgba(148, 163, 184, 0.3)',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Bulk Analyze
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="outlined"
                startIcon={<YouTube />}
                href={`https://www.youtube.com/channel/${channelId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: 3,
                  padding: '8px 20px',
                  borderColor: 'rgba(148, 163, 184, 0.3)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'rgba(148, 163, 184, 0.5)',
                    background: 'rgba(148, 163, 184, 0.1)',
                  },
                }}
              >
                View on YouTube
              </Button>
            </Box>
          </Box>

          {/* Success/Error Messages */}
          {success && (
            <Alert
              severity="success"
              sx={{ mt: 2, borderRadius: 2 }}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Sorting & Search Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                <MenuItem value="publishedTime">Published Date</MenuItem>
                <MenuItem value="outlierScore">Outlier Score</MenuItem>
                <MenuItem value="viewCount">View Count</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Published Date</InputLabel>
              <Select
                value={dateFilter}
                label="Published Date"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="2weeks">Last 2 Weeks</MenuItem>
                <MenuItem value="3weeks">Last 3 Weeks</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="2months">Last 2 Months</MenuItem>
                <MenuItem value="3months">Last 3 Months</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
                <MenuItem value="3years">Last 3 Years</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Min Outlier Score</InputLabel>
              <Select
                value={minOutlierScore}
                label="Min Outlier Score"
                onChange={(e) => setMinOutlierScore(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="5">5</MenuItem>
                <MenuItem value="10">10</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search title"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Chip
              label={`${videos.length}${totalVideos > 0 ? ` / ${totalVideos}` : ''} Videos (${
                {
                  all: 'All Time',
                  week: 'Last Week',
                  '2weeks': 'Last 2 Weeks',
                  '3weeks': 'Last 3 Weeks',
                  month: 'Last Month',
                  '2months': 'Last 2 Months',
                  '3months': 'Last 3 Months',
                  '6months': 'Last 6 Months',
                  year: 'Last Year',
                  '3years': 'Last 3 Years',
                  '5years': 'Last 5 Years',
                }[dateFilter] || 'All Time'
              })`}
              sx={{ ml: 'auto' }}
            />
          </Box>

          {videos.length === 0 ? (
            <StyledEmptyState>
              <StyledEmptyStateIcon>
                <VideoLibrary sx={{ fontSize: 40, color: 'text.secondary' }} />
              </StyledEmptyStateIcon>
              <Typography variant="h6" color="text.secondary" mb={1}>
                No videos yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Videos will appear after they are fetched from this channel
              </Typography>
            </StyledEmptyState>
          ) : (
            <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
              {videos.map((video) => (
                <Grid item key={video._id} sx={{ width: '24%', flexBasis: '24%', maxWidth: '24%' }}>
                  <VideoCard
                    video={video}
                    highlightQuery={debouncedQuery}
                    showTranslation={showTranslation}
                    analyticsContext={{
                      scope: 'channel',
                      channelId,
                      channelTitle: channel?.channelName || video.channelName,
                      searchSnapshot,
                    }}
                  />
                </Grid>
              ))}
              {!hasMore && videos.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      All videos loaded
                    </Typography>
                  </Box>
                </Grid>
              )}
              {hasMore && (
                <Grid item xs={12} ref={loadMoreRef}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    {loadingMore && <CircularProgress size={24} />}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </StyledPaper>

        {/* Bulk Analyze Modal */}
        <BulkAnalyzeModal
          open={bulkAnalyzeModalOpen}
          onClose={handleBulkAnalyzeClose}
          videos={videos}
          showTranslation={showTranslation}
          onSuccess={(message) => {
            setSuccess(message);
            setTimeout(() => setSuccess(''), 5000);
          }}
          onError={(message) => {
            setError(message);
            setTimeout(() => setError(''), 5000);
          }}
          onRefresh={fetchChannelData}
        />
      </StyledContainer>
    </Container>
  );
}

export default Channel;
